import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { Checkbox, Col, Form, Input, Row } from "antd";
import { useMemo } from "react";
import { TRANSLATIONS_PROPERTY_NAME } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, EUserRole, ServiceTemplateField } from "@devowl-wp/api-real-cookie-banner";
import { useTranslatePurposeKey } from "./useQuestions.js";
import { useStores } from "../../../../../../../store/root.js";
import { mergeStyles } from "../../../../../../../util/style.js";
import { getTranslationFlagElementStyle, hasTranslationFlagsChanged, onTranslationValueChange } from "../../../../../../../util/translation.js";
import { ColumnToolTipHeader } from "../../../../../../table/column-tooltip-header.js";
import { LanguageIcon } from "../../../fields/translation/language-icon-addon/language-icon.js";
import { TranslationTextAreaFormFields } from "../../../fields/translation/language-text-area-input/language-text-area-form-fields.js";
function useServiceTemplatePurposeTableColumns(t, purposeOptions) {
    const { rcbAuthenticationStore } = useStores();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const { TextArea } = Input;
    const textAreaStyle = {
        flex: "1 1 500px"
    };
    const purposeQuestionKeys = useMemo(()=>{
        return purposeOptions.map((option)=>option.questionKey);
    }, [
        purposeOptions
    ]);
    const form = Form.useFormInstance();
    return [
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Question / Answer"),
                tooltipTitle: t("Select or enter custom questions and provide an answer to them.")
            }),
            dataIndex: "question",
            render: (_, record, index)=>/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Row, {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                noStyle: true,
                                shouldUpdate: (prev, next)=>prev[record.name]?.question !== next[record.name]?.question,
                                children: ({ getFieldValue })=>{
                                    const questionOrKey = getFieldValue([
                                        ServiceTemplateField.Purposes,
                                        record.name,
                                        "question"
                                    ]);
                                    const isEditable = purposeQuestionKeys.findIndex((predefined)=>predefined === questionOrKey) < 0;
                                    const position = index + 1;
                                    return isEditable ? /*#__PURE__*/ _jsxs("div", {
                                        style: {
                                            display: "flex",
                                            alignItems: "flex-start",
                                            width: "100%"
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                style: {
                                                    marginRight: 8
                                                },
                                                children: `${position})`
                                            }),
                                            /*#__PURE__*/ _createElement(Form.Item, {
                                                ...record,
                                                key: "question",
                                                name: [
                                                    record.name,
                                                    "question"
                                                ],
                                                style: {
                                                    flex: 1
                                                }
                                            }, /*#__PURE__*/ _jsx(Input, {
                                                disabled: !hasCreatorRole
                                            }))
                                        ]
                                    }) : /*#__PURE__*/ _jsxs("div", {
                                        style: {
                                            display: "flex",
                                            alignItems: "flex-start",
                                            width: "100%"
                                        },
                                        children: [
                                            /*#__PURE__*/ _createElement(Form.Item, {
                                                hidden: true,
                                                ...record,
                                                key: "question",
                                                name: [
                                                    record.name,
                                                    "question"
                                                ],
                                                style: {
                                                    flex: 1
                                                }
                                            }, /*#__PURE__*/ _jsx(Input, {})),
                                            `${position}) ${useTranslatePurposeKey(t, questionOrKey)}`
                                        ]
                                    });
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsx(Form.Item, {
                            noStyle: true,
                            shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                                    ServiceTemplateField.Purposes,
                                    record.name,
                                    "answer"
                                ]),
                            children: ({ validateFields })=>{
                                const onBlurAnswer = ()=>validateFields([
                                        ServiceTemplateField.Purposes
                                    ]);
                                const relativePath = [
                                    record.name,
                                    "answer"
                                ];
                                const absolutePath = [
                                    ServiceTemplateField.Purposes,
                                    record.name,
                                    "answer"
                                ];
                                const flagStyle = getTranslationFlagElementStyle(form, absolutePath);
                                const dynamicStyle = mergeStyles(textAreaStyle, flagStyle);
                                return /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(Row, {
                                            gutter: [
                                                10,
                                                10
                                            ],
                                            children: [
                                                /*#__PURE__*/ _jsx(Col, {
                                                    flex: 1,
                                                    children: /*#__PURE__*/ _createElement(Form.Item, {
                                                        ...record,
                                                        key: "answer",
                                                        name: relativePath,
                                                        noStyle: true
                                                    }, /*#__PURE__*/ _jsx(TextArea, {
                                                        placeholder: t("Enter your answer here ..."),
                                                        style: dynamicStyle,
                                                        onChange: ()=>onTranslationValueChange(form, absolutePath),
                                                        rows: 2,
                                                        onBlur: onBlurAnswer,
                                                        disabled: !hasCreatorRole
                                                    }))
                                                }),
                                                /*#__PURE__*/ _jsx(Col, {
                                                    flex: "0",
                                                    children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                                        lang: DEFAULT_LANGUAGE,
                                                        flexPathConfig: {
                                                            isFormListItem: true,
                                                            relative: {
                                                                namePath: relativePath,
                                                                namePathPrefix: ServiceTemplateField.Purposes,
                                                                translationNamePath: (lang)=>[
                                                                        ServiceTemplateField.Purposes,
                                                                        record.name,
                                                                        TRANSLATIONS_PROPERTY_NAME,
                                                                        lang,
                                                                        "answer"
                                                                    ]
                                                            }
                                                        }
                                                    })
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(TranslationTextAreaFormFields, {
                                            flexPathConfig: {
                                                isFormListItem: true,
                                                relative: {
                                                    namePath: (lang)=>[
                                                            record.name,
                                                            TRANSLATIONS_PROPERTY_NAME,
                                                            lang,
                                                            "answer"
                                                        ],
                                                    customFlagNamePath: (lang)=>[
                                                            ServiceTemplateField.Purposes,
                                                            record.name,
                                                            TRANSLATIONS_PROPERTY_NAME,
                                                            lang,
                                                            "answer"
                                                        ]
                                                }
                                            },
                                            textAreaProps: {
                                                rows: 2,
                                                onBlur: onBlurAnswer
                                            }
                                        })
                                    ]
                                });
                            }
                        })
                    ]
                })
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Paragraph"),
                tooltipTitle: t("Check this box to display the answer in the same row in a new paragraph.")
            }),
            dataIndex: "isNewParagraph",
            render: (_, record, index)=>/*#__PURE__*/ _jsxs("div", {
                    style: {
                        textAlign: "center"
                    },
                    children: [
                        /*#__PURE__*/ _createElement(Form.Item, {
                            ...record,
                            key: "isNewParagraph",
                            name: [
                                record.name,
                                "isNewParagraph"
                            ],
                            valuePropName: "checked",
                            hidden: index === 0
                        }, /*#__PURE__*/ _jsx(Checkbox, {
                            disabled: !hasCreatorRole
                        })),
                        index === 0 && "-"
                    ]
                })
        }
    ];
}
export { useServiceTemplatePurposeTableColumns };
